import type { FC } from 'react';
import React, { useState, useCallback, useEffect, useRef, forwardRef } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl-next';
import { styled, css } from '@compiled/react';

import ShowMoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Blanket from '@atlaskit/blanket';
import type { PopupComponentProps } from '@atlaskit/popup';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import type { TooltipPrimitiveProps } from '@atlaskit/tooltip';
import FilterIcon from '@atlaskit/icon/core/filter';
import { token } from '@atlaskit/tokens';
import { IconButton, type IconButtonProps } from '@atlaskit/button/new';

import { useSSRPlaceholderReplaceIdProp, LoadableLazy } from '@confluence/loadable';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';
import { useIsMultiSelectActive } from '@confluence/content-tree-actions/entry-points/useIsMultiSelectActive.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';

import { useOnScreen } from './useOnScreen.ts';
import type { SpaceViewsPersistenceOption } from './__generated__/useSpaceViewsOptionsPersistenceQuery.graphql.ts';

const SpaceViewsMenuContentLoader = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-SpaceViewsMenuContent" */ './SpaceViewsMenuContent.tsx'
			)
		).SpaceViewsMenuContent,
});

const useLazyOptionClickedAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ './analyticsCallbacks.ts'))
			.fireSpaceViewsOptionClickedAnalytics,
);

const useLazyTriggerClickedAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ './analyticsCallbacks.ts'))
			.fireSpaceViewsTriggerClickedAnalytics,
);

const i18n = defineMessages({
	menuTooltipForContent: {
		id: 'space-views.space.menu.tooltip.content',
		defaultMessage: 'Change view',
		description: "tooltip for the '...' menu shown next to the pages link in the sidebar",
	},
	disabledMenuTooltipForContent: {
		id: 'space-views.space.menu.tooltip.content.disabled',
		defaultMessage: 'Filtering is disabled when editing multiple items',
		description:
			'Tooltip for the space views menu button when the button is disabled due to the multi-select mode',
	},
});

type MenuProps = {
	spaceId?: string;
	isWhiteboardBetaEnabled: boolean;
	edition?: string;
	isSpaceAdminOrSuperAdmin: boolean;
	triggerRef?: React.RefObject<HTMLButtonElement>;
};

export type SetSelection = (option: SpaceViewsPersistenceOption) => void;

export type PopupProps = {
	selection: string;
	setSelection: SetSelection;
};

// FG + fallback: two static components (no fg at module level – satisfies confluence-feature-gating/no-module-level-eval and no-unsafe-values)
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const CustomPopupContainerWithToken = styled.div({
	zIndex: 1000,
	backgroundColor: token('elevation.surface.overlay'),
	boxShadow: token('elevation.shadow.overlay'),
	borderRadius: token('radius.small'),
	'&:focus': {
		outline: 'none',
	},
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const CustomPopupContainerWithoutToken = styled.div({
	zIndex: 1000,
	backgroundColor: token('elevation.surface.overlay'),
	boxShadow: token('elevation.shadow.overlay'),
	borderRadius: '3px',
	'&:focus': {
		outline: 'none',
	},
});

// same styles as a tooltip but with narrower width (FG + fallback for borderRadius)
const tooltipStylesWithToken = css({
	background: `${token('color.background.neutral.bold')}`,
	color: `${token('color.text.inverse')}`,
	borderRadius: token('radius.small'),
	font: token('font.body.small'),
	padding: '2px 6px',
	maxWidth: '191px',
});
const tooltipStylesWithoutToken = css({
	background: `${token('color.background.neutral.bold')}`,
	color: `${token('color.text.inverse')}`,
	borderRadius: '3px',
	font: token('font.body.small'),
	padding: '2px 6px',
	maxWidth: '191px',
});

const ToolTipOverride = forwardRef<
	HTMLDivElement,
	TooltipPrimitiveProps & { tooltipStyles?: ReturnType<typeof css> }
>(function ToolTipOverride({ children, tooltipStyles, ...props }, ref) {
	return (
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
		<TooltipPrimitive {...props} ref={ref} css={tooltipStyles ?? tooltipStylesWithToken}>
			{children}
		</TooltipPrimitive>
	);
}) as React.ForwardRefExoticComponent<
	TooltipPrimitiveProps & { tooltipStyles?: ReturnType<typeof css> }
>;

export const MenuComponent: FC<MenuProps & PopupProps & WrappedComponentProps> = ({
	selection,
	setSelection,
	intl,
	isWhiteboardBetaEnabled,
	edition,
	isSpaceAdminOrSuperAdmin,
	triggerRef: externalTriggerRef,
}) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const [isOpen, setIsOpen] = useState(false);
	const [isMultiSelectActive] = useIsMultiSelectActive();

	const handleClose = useCallback(() => {
		setIsOpen(false);
	}, [setIsOpen]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const ref = useRef<HTMLDivElement>(null);

	const isVisible = useOnScreen(ref);

	useEffect(() => {
		if (!isVisible) {
			if (isOpen) {
				setIsOpen(false);
			}
		}
	}, [isVisible, isOpen]);

	const fireOptionClickedAnalytics = useLazyOptionClickedAnalytics(createAnalyticsEvent, selection);
	const setAndClose = useCallback(
		(option: any) => {
			setSelection(option);
			setIsOpen(false);
			void fireOptionClickedAnalytics(option);
		},
		[setSelection, fireOptionClickedAnalytics],
	);

	const fireTriggerClickedAnalytics = useLazyTriggerClickedAnalytics(
		createAnalyticsEvent,
		selection,
	);
	const onTriggerClick = useCallback(() => {
		setIsOpen((o) => !o);
		void fireTriggerClickedAnalytics();
	}, [fireTriggerClickedAnalytics]);

	const CustomPopupContainer = forwardRef<HTMLDivElement, PopupComponentProps>(
		({ children, ...props }, ref) =>
			fg('confluence_frontend_tokenize_border') ? (
				<CustomPopupContainerWithToken {...props} ref={ref}>
					{children}
				</CustomPopupContainerWithToken>
			) : (
				<CustomPopupContainerWithoutToken {...props} ref={ref}>
					{children}
				</CustomPopupContainerWithoutToken>
			),
	);

	const tooltipOptions: IconButtonProps['tooltip'] = {
		position: 'bottom-end',
		component: (p) => (
			<ToolTipOverride
				{...p}
				tooltipStyles={
					fg('confluence_frontend_tokenize_border')
						? tooltipStylesWithToken
						: tooltipStylesWithoutToken
				}
			/>
		),
	};

	const getTriggerButton = (triggerProps: any) => {
		// Combine the popup's ref with external ref (if provided)
		const combinedRef = (element: HTMLButtonElement | null) => {
			// Set the popup system's ref
			if (triggerProps.ref) {
				if (typeof triggerProps.ref === 'function') {
					triggerProps.ref(element);
				} else {
					triggerProps.ref.current = element;
				}
			}
			// Set external ref if provided - this connects our parent's ref to the DOM element
			if (externalTriggerRef) {
				(externalTriggerRef as React.MutableRefObject<HTMLButtonElement | null>).current = element;
			}
		};

		if (
			edition === 'PREMIUM' &&
			isSpaceAdminOrSuperAdmin &&
			expVal('confluence_bulk_actions_in_content_tree', 'isEnabled', false)
		) {
			return (
				<IconButton
					{...triggerProps}
					ref={combinedRef}
					icon={FilterIcon}
					onClick={onTriggerClick}
					isSelected={isOpen}
					label={
						isMultiSelectActive
							? intl.formatMessage(i18n.disabledMenuTooltipForContent)
							: intl.formatMessage(i18n.menuTooltipForContent)
					}
					isDisabled={isMultiSelectActive}
					isTooltipDisabled={false}
					tooltip={tooltipOptions}
				/>
			);
		}
		return (
			<IconButton
				{...triggerProps}
				ref={combinedRef}
				icon={(iconProps) => <ShowMoreIcon {...iconProps} size="small" />}
				onClick={onTriggerClick}
				isSelected={isOpen}
				label={intl.formatMessage(i18n.menuTooltipForContent)}
				spacing="compact"
				appearance="subtle"
			/>
		);
	};

	return (
		<div ref={ref} data-vc="space-views-menu" {...ssrPlaceholderIdProp}>
			{isOpen && (
				<Blanket
					isTinted={false}
					shouldAllowClickThrough={!isOpen}
					onBlanketClicked={handleClose}
					testId="space-views-menu-blanket"
				/>
			)}
			<Popup
				isOpen={isOpen}
				onClose={handleClose}
				content={(props) => (
					<SpaceViewsMenuContentLoader
						selection={selection}
						setSelection={setAndClose}
						intl={intl}
						{...props}
					/>
				)}
				popupComponent={CustomPopupContainer}
				trigger={(triggerProps) => (
					<>
						<Tooltip
							content={<FormattedMessage {...i18n.menuTooltipForContent} />}
							position={isWhiteboardBetaEnabled ? 'top' : 'bottom'}
							hideTooltipOnMouseDown
						>
							{getTriggerButton(triggerProps)}
						</Tooltip>
					</>
				)}
				placement="bottom-start"
				shouldRenderToParent
			/>
		</div>
	);
};

export const SpaceViewsMenu = injectIntl(MenuComponent);
